import { EP } from "../../performance/PerfomanceItem";
import { KnxChapterItem } from "./KnxChapterItem";
import Channel, { ChannelTypeProperty } from "../../models/Channel";
import Logger from "../../Logger";
import type KnxContext from "./KnxContext";
import { isParameterType, isTypeFloat, isTypeNumber, isTypeRestriction } from "../../Types";
import { isUiModeDeveloper, isUiModeProductAdmin } from "../../uiMode";
import { firstChildWithTag, getRefId } from "../XmlUtils";
import KnxProperty2, { buildKnxParameterValueList } from "./KnxProperty2";
import KnxPropertyIndicator from "./KnxPropertyIndicator";
import { getChannelTypeForEnum } from "../../KnxMappings/ButtonChannelType";
import ButtonChannel from "../../models/ButtonChannel";
import { ChannelType } from "../../models/ChannelType";
import Signal from "../../models/Signal";
import { noItx } from "../../translationItx/itx";
import SceneNumberElement from "./SceneNumberElement";
import { ValueLevels } from "../../models/KnxInstanceData";
import KnxDebugElement from "./KnxDebugElement";
import KnxTypePicture from "./KnxTypePicture";
import { useEffect, useState } from "react";

const logger = new Logger();
let propertyIndex = 0;
let lastChannelTypePropertyPartameterType = "";

function ProductAdminInfo(props: { knxContext: KnxContext; refId: string; instanceId: string }) {
  const { knxContext, refId, instanceId } = props;

  const [val1, setVal1] = useState(knxContext.getValue(refId, instanceId));
  const [val2, setVal2] = useState(knxContext.getValue(refId, instanceId, false, true));

  if (!isUiModeProductAdmin()) {
    return null;
  }

  let xval = val1;
  if (val1 != val2) {
    xval = `${val1} / ${val2}`;
  }

  const currentLevel = knxContext.knxInstanceData.getLevel(refId || "", instanceId || "");

  return (
    <div
      className="knxAdminInfo"
      onMouseEnter={() => {
        setVal1(knxContext.getValue(refId, instanceId));
        setVal2(knxContext.getValue(refId, instanceId, false, true));
      }}
    >
      <div className="estadaRow">
        {noItx("id=")} {refId}
        <button
          className="button small circle"
          onClick={() => {
            navigator.clipboard.writeText(refId || "");
          }}
        >
          <i className="las la-copy"></i>
        </button>
      </div>
      {instanceId && (
        <div className="estadaRow">
          <span className="hover">{noItx("instanceId")}</span>
          <span className="nohover">i</span>= {instanceId}
          <button
            className="button small circle"
            onClick={() => {
              navigator.clipboard.writeText(instanceId || "");
            }}
          >
            <i className="las la-copy"></i>
          </button>
        </div>
      )}
      {noItx("val=")} {xval} {noItx("level=")} {currentLevel}
    </div>
  );
}

function checkDouble(rawSceneData: Array<any> | undefined, id: string): boolean {
  var res = false;

  rawSceneData?.forEach((item) => {
    if (item.id == id) {
      res = true;
      logger.log("duplicate sceneData ", item);
      return;
    }
  });

  return res;
}

function handleChannelTypeParameter(params: {
  knxContext: KnxContext;
  parentChannel: Channel;
  data: any;
  id: string;
  instanceId: string;
  moduleId: string;
}): boolean {
  const { knxContext, parentChannel, data, id, instanceId, moduleId } = params;

  //
  // check if a channelType is mapped to this property
  //
  const props = knxContext.device?.mapChannelTypeConfig?.props;
  if (props) {
    for (const prop of props) {
      if (prop.id == id) {
        let channelTypeProperty = new ChannelTypeProperty();
        channelTypeProperty.id = id;
        channelTypeProperty.instanceId = instanceId;
        channelTypeProperty.moduleId = moduleId;

        // currentChannelTypeProperty = channelTypeProperty;

        knxContext.knxInstanceData.setExtendedData({ channelTypeProperty: channelTypeProperty }, id, instanceId);

        return true;
      }
    }
  }

  const parameterType = knxContext.knxProductData.getParameterType(data);
  if (parameterType == undefined) {
    return false;
  }
  // const nameIncludesFunc: boolean = [".*func.*", ".*rocker.*select.*"].some((s) =>
  //   pameterType.a.Name.toLowerCase().match(s),
  // );

  if (parameterType.a.Name.toLowerCase().includes("led")) {
    return false;
  }

  //
  // Skip status displays to diverse Types
  // z.B. MDT BE-TAS86.01
  //
  const parameterName = data.a.Name.toLowerCase();
  if (!parameterName.includes("function of icon")) {
    // for GVS Z10
    if (parameterName.includes("status") || parameterName.includes("state") || parameterName.includes("icon")) {
      return false;
    }
  }

  // const paramNameEN = knxContext.knxProductData.getTranslatedProperty("Name", parameterType, true).toLowerCase();

  // const nameIncludesFunc = matchN(["func", "select", "funktion"], paramNameEN);
  // if (!nameIncludesFunc) {
  //   return false;
  // }

  const innerItem = parameterType?.items[0];
  if (!isTypeRestriction(innerItem)) {
    return false;
  }
  const typeRestriction = innerItem;
  let channelTypeProperty = new ChannelTypeProperty();
  channelTypeProperty.id = id;
  channelTypeProperty.instanceId = instanceId;
  channelTypeProperty.moduleId = moduleId;
  let toggleFound = false;

  for (const item of typeRestriction.items) {
    const textEN = knxContext.knxProductData.getTranslatedProperty("Text", item, true);
    const textLocal = knxContext.knxProductData.getTranslatedProperty("Text", item, false);

    let { channelType, isToggle } = getChannelTypeForEnum(textEN);

    if (channelType != ChannelType.Unknown) {
      //
      // Achtung: MWE 12.2.2025
      //
      // für Buttons die sowohl "toggle"  als auch "switch" unterstützen
      // verwenden wir "toggle" als switch und ignorieren reine switchwerte
      if (channelType == ChannelType.Switch) {
        if (isToggle) {
          toggleFound = true;
        } else {
          if (toggleFound) {
            continue;
          }
        }
      }

      channelTypeProperty.mapChannelTypeToValue.set(channelType, {
        optionValue: item.a.Value,
        optionDescription: textLocal,
      });
    }
  }
  // check here if only disabled
  if (
    channelTypeProperty.mapChannelTypeToValue.size == 2 &&
    channelTypeProperty.mapChannelTypeToValue.get(ChannelType.Disabled)
  ) {
    return false;
  }
  if (channelTypeProperty.mapChannelTypeToValue.size > 1) {
    const alreadyFound = knxContext.knxInstanceData.getExtendedData(id, instanceId)?.channelTypeProperty;
    if (!alreadyFound) {
      knxContext.knxInstanceData.setExtendedData({ channelTypeProperty: channelTypeProperty }, id, instanceId);
    }
    return true;
  }
  return false;
}

function handleSceneParameter(params: {
  knxContext: KnxContext;
  parentChannel: Channel;
  data: any;
  id: string;
  instanceId: string;
  moduleId: string;
  nestedName: string;
  nestedText: string;
}) {
  const { knxContext, parentChannel, data, id, instanceId, nestedName, nestedText, moduleId } = params;

  if (!knxContext.knxProductData.supportScenes) {
    return;
  }

  //
  // detect SceneNumber Property for Button
  //

  let isSceneNumberProperty = false;

  if (knxContext.device?.mapChannelTypeConfig?.sceneNumber) {
    isSceneNumberProperty = knxContext.device.mapChannelTypeConfig.sceneNumber.id == id;
  } else {
    isSceneNumberProperty = knxContext.knxProductData?.isSceneNumberItem(data);
  }

  if (!knxContext.isActor) {
    if (isSceneNumberProperty) {
      // logger.log(`sceneNumber property found  ${data.a.RefId}`);
      knxContext.currentKnxButtonSceneNumberProperty = (parentChannel as ButtonChannel).knxButtonSceneNumberProperty = {
        id: id,
        instanceId: instanceId,
        moduleId: moduleId,
      };

      if (knxContext.lastSceneSignal && !knxContext.lastSceneSignal.knxSceneNumberProperty) {
        knxContext.lastSceneSignal.knxSceneNumberProperty = knxContext.currentKnxButtonSceneNumberProperty;
        knxContext.currentKnxButtonSceneNumberProperty = undefined;
      }
    }
  }

  let xIsSceneItem = isSceneNumberProperty;

  if (knxContext.isActor) {
    let xIsSceneItem1 = knxContext.isSceneItem(data);
    xIsSceneItem = isSceneNumberProperty || xIsSceneItem1;
  }

  if (xIsSceneItem) {
    if (knxContext.isActor) {
      if (!checkDouble(knxContext.currentRawSceneData, id)) {
        if (!knxContext.currentRawSceneData) {
          knxContext.currentRawSceneData = [];

          const dummySignal = new Signal({}, parentChannel);
          dummySignal.knxTextENForDetection_Template = dummySignal.name = noItx("@DUMMY-SceneData@");
          dummySignal.knxFunctionTextEN = noItx("DUMMY SceneData");

          knxContext.lastSignalNumber = knxContext.lastSignalNumber + 0.1;
          dummySignal.number = `${knxContext.lastSignalNumber}`;
          dummySignal.knxChannelTypeProperty = knxContext.currentChannelTypeProperty;
          dummySignal.rawSceneData = knxContext.currentRawSceneData;

          knxContext.xSignals.push(dummySignal);
        }

        knxContext.currentRawSceneData.push({
          a: data.a,
          id: id,
          instanceId: instanceId,
          moduleId: moduleId,
          isSceneNumber: isSceneNumberProperty,
          index: knxContext.rawSceneItemIndex++,
        });
      }
    }

    // if isSet == true, keep it
    const currentValueExt = knxContext.knxInstanceData.getExtendedData(id, instanceId);
    const mbe = currentValueExt?.mbe;

    knxContext.knxInstanceData.setExtendedData(
      {
        mbe: {
          isSet: false,
          ...mbe,
        },
        isSceneItem: true,
        isSceneNumberProperty: isSceneNumberProperty,
      },
      id,
      instanceId,
    );

    // logger.log(
    //   `add raw scene data: channel:${channel.name} `,
    //   data,
    //   defItem,
    // );
  }
}

function Parameter(params: {
  knxContext: KnxContext;
  data: any;
  args: any;
  repeatindex?: number;
  instanceId: string;
  moduleId: string;
  chapterId: string;
  parentKnxChapter: KnxChapterItem | undefined;
  nestedName: string;
  nestedText: string;
  parentChannel: Channel;
  indent: number;
  hidden: boolean;
}) {
  var { knxContext, data, args, repeatindex, instanceId, moduleId, chapterId, nestedName, parentChannel, indent } =
    params;

  const currentTable = knxContext.currentTable;
  const flatItems = knxContext.flatItems;
  let element: any = undefined;

  if (data == undefined) {
    return null;
  }

  var itemRef = data;

  const refId = itemRef.a.RefId;

  var currentValue = knxContext.getValue(refId, instanceId, false, true);

  const PT = knxContext.deref(data.a.ParameterType);

  let defaultValue: string | undefined = "?";

  if (instanceId && itemRef.a.BaseValue) {
    //
    // MWE 3.6.2026
    // wir haben einen Parameter innerhaölb einer Modulinstanz, der einen BaseValue hat.
    // den haben wir bereits in ApplyModuleDefinition/setDefaultsFromParameterDefinition gesetzt.
    //
    // Ob es sinnvoll hier überhaupt default werte zu setzen ist,
    // oder ob die vorher "global" gestezt werden müssten ist seit jeher unklar.
    //
    //
  } else {
    defaultValue = knxContext.device.template?.knxProductData.getDefaultValueForParameter(itemRef, instanceId);
    // console.log("defaultValue", itemRef, instanceId, defaultValue);

    const TN = PT?.items[0];

    if (TN && isTypeFloat(TN)) {
      const n = Number(defaultValue);

      defaultValue = n.toString();
    }

    if (defaultValue != undefined) {
      knxContext.setValueNoRerender(ValueLevels.default, defaultValue, refId, instanceId, moduleId);
      currentValue = defaultValue;
    }
  }

  knxContext.knxInstanceData.setReferenced(refId, instanceId, true); // no itx

  if (isParameterType(PT)) {
    const TN = PT.items[0];
    if (isTypeNumber(TN)) {
      const max = parseInt(TN.a.maxInclusive);
      const min = parseInt(TN.a.minInclusive);
      if (currentValue > max) {
        knxContext.setValueNoRerender(ValueLevels.keepLevel, max, refId, instanceId, moduleId);
        currentValue = max;
      }
      if (currentValue < min) {
        knxContext.setValueNoRerender(ValueLevels.keepLevel, min, refId, instanceId, moduleId);
        currentValue = min;
      }
    }
  }

  var itemRef = data;

  const item = knxContext.deref(itemRef);
  var text = knxContext.getText(itemRef);

  text = knxContext.substitutePlaceholders({
    input: text,
    args: args,
    repeatindex: repeatindex,
    params: params,
    instanceId: instanceId,
    moduleId: moduleId,
  });

  const TN = PT?.items[0];
  if (TN && isTypeFloat(TN)) {
    const n = Number(currentValue);

    const language = state.settings.uiLanguage;
    const localized = n.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    currentValue = localized;
  }

  const access = item.a.Access;
  const userHasAccess = access != "None";
  const readonlyAccess = access == "Read";
  let isChannelTypeProperty = false;

  // if (!hidden && userHasAccess) {
  if (userHasAccess && !readonlyAccess) {
    //
    // (*A) siehe ein paar Zeilen tiefer
    //
    if (lastChannelTypePropertyPartameterType == item.a.ParameterType) {
      lastChannelTypePropertyPartameterType = "";
    }

    if (
      knxContext.device?.mapChannelTypeConfig ||
      (!lastChannelTypePropertyPartameterType && !knxContext.isActor && !knxContext.currentChannelTypeProperty)
    ) {
      //
      // MWE 2.12.2025
      //
      // we don't allow nested ChannelTypeProperties zb. MDT Touch.
      //
      // if we have to ChannelTypeProperties in a row,
      // only the first is valid
      // the secound one is ignored
      //

      // MWE 9.3.2026
      //
      // das funktioniert aber nicht für Merten "KNX-Tastsensor Flex System Design | 4-fach | schwarz	MEG6194-6010"
      // Der hat in der Tat die liste aller ChannelTypen hinter einander!!!
      //
      // Daher lassen wir eine Liste von ChannelTypeProperties hintereinander zu,
      // wenn sie alle vom selben Type sind: (*A)
      //
      // Im Falle von MDT Touch haben wir als erstes den echen ChannelType
      // und dann noch eine Art UnterType, den wir nicht erkennen wollen.
      // Die haben dann aber eben auch unterschiedliceh Auswahllisten,
      // das heißt unterschiedliche TYpen.
      //
      //

      isChannelTypeProperty = handleChannelTypeParameter({ ...params, id: refId, data: item });
      if (isChannelTypeProperty) {
        lastChannelTypePropertyPartameterType = item.a.ParameterType;
      } else {
        lastChannelTypePropertyPartameterType = "";
      }
    } else {
      lastChannelTypePropertyPartameterType = "";
    }

    if (!isChannelTypeProperty) {
      handleSceneParameter({ ...params, id: refId, data: item });
    }
  }
  if (access == "None") {
    knxContext.knxInstanceData.setExtendedData({ access: "None" }, refId, instanceId); // no itx
  }

  knxContext.setMemoryInfo?.(repeatindex, instanceId, item, args);

  var currentValueExt = knxContext.knxInstanceData.getExtendedData(refId, instanceId);

  const ParameterTypeId = item.a.ParameterType;

  const parmeterType = knxContext.knxProductData?.globalMap.get(ParameterTypeId);

  const noType = parmeterType?.TypeNone;

  if (noType) {
    return null;
  }

  if (userHasAccess || isUiModeDeveloper()) {
    var xid = refId;
    if (instanceId != "") {
      xid = instanceId + "|" + refId;
    }

    const valueList = buildKnxParameterValueList(parmeterType, knxContext.knxProductData, knxContext.device);

    let inputElement = (
      <KnxProperty2
        device={knxContext.device}
        item={item}
        refId={refId}
        instanceId={instanceId}
        chapterId={chapterId}
        isCell={currentTable != undefined}
        onChange={() => {
          knxContext.device.template?.knxProductData.currentRules?.pullAllValues(knxContext.device, {});
        }}
      />
    );

    var debugElement =
      isUiModeDeveloper() && !currentTable ? (
        <KnxDebugElement
          item={item}
          nestedName={nestedName}
          id={refId}
          instanceId={instanceId}
          knxContext={knxContext}
        />
      ) : null;

    //   <ParameterType Id="M-0083_A-003D-25-CE9E_PT-Ch.202.20parallel" Name="Ch 2 parallel">
    //   <TypePicture RefId="M-0083_BG--2.20parallel.5F75.25.2EPNG" />
    // </ParameterType>
    ///////////////////////////////////////////////////////////////////////////////////
    let propertyIndicator = readonlyAccess ? undefined : (
      <KnxPropertyIndicator
        device={knxContext.device}
        index={propertyIndex++}
        label={text}
        defaultValue={defaultValue}
        valueList={valueList}
        refId={refId}
        instanceId={instanceId}
        moduleId={moduleId}
        isSceneItem={!isChannelTypeProperty && knxContext.isSceneItem(data)}
        isChannelTypeProperty={isChannelTypeProperty}
        isInCell={currentTable}
      />
    );

    const typePicture = firstChildWithTag(parmeterType, "TypePicture"); // no itx
    if (typePicture) {
      element = (
        <KnxTypePicture
          key={xid}
          imgRef={typePicture.a.RefId}
          title={text}
          debugElement={debugElement}
          isCell={currentTable != undefined}
        />
      );
    } else {
      if (currentTable) {
        element = (
          <div key={xid} className="knxValue">
            {propertyIndicator}
            {/* <ProductAdminInfo
              knxContext={knxContext}
              refId={refId}
              instanceId={instanceId}
              currentValue={currentValue}
            /> */}

            {!noType && (
              <SceneNumberElement
                id={refId}
                instanceId={instanceId}
                moduleId={moduleId}
                inputElement={inputElement}
                value={currentValue}
                knxContext={knxContext}
              />
            )}
          </div>
        );
      } else {
        element = (
          <div
            key={xid}
            className="knxRow"
            style={{ backgroundColor: !userHasAccess ? "#fdd" : undefined }} // no itx
          >
            {propertyIndicator}
            <div className="knxLabel">{text}</div>

            <ProductAdminInfo
              key={"adminInfo-" + currentValue}
              knxContext={knxContext}
              refId={refId}
              instanceId={instanceId}
            />

            <div style={{ display: "flex" }}>{debugElement}</div>

            {!noType && (
              <SceneNumberElement
                id={refId}
                instanceId={instanceId}
                moduleId={moduleId}
                inputElement={inputElement}
                value={currentValue}
                knxContext={knxContext}
              />
            )}
          </div>
        );
      }

      knxContext.knxInstanceData.setExtendedData(
        {
          info: {
            name: data.a.Name,
            text: knxContext.knxProductData.getTranslatedProperty("Text", item, false),
            textEN: knxContext.knxProductData.getTranslatedProperty("Text", item, true),
            typeId: parmeterType?.a.Id,
            chapterName: parentChannel.name,
          },
        },
        refId,
        instanceId,
      );

      if (data.a.DisplayOrder && flatItems) {
        flatItems.push({
          name: text,
          type: "P",
          order: parseInt(data.a.DisplayOrder),
          elm: element,
          indent: indent,
          id: xid,
        });
      }
    }

    if (currentTable) {
      const cell = item.a.Cell;
      if (cell) {
        const ii = cell.split(",");

        if (!currentTable.cellDatas[ii[0]]) {
          currentTable.cellDatas[ii[0]] = [];
        }
        currentTable.cellDatas[ii[0]][ii[1]] = {
          id: refId,
          instanceId: instanceId,
        };

        /////////////////////////////////////////////////////////////

        if (!currentTable.cells[ii[0]]) {
          currentTable.cells[ii[0]] = [];
        }

        currentTable.cells[ii[0]][ii[1]] = (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              height: isUiModeProductAdmin() || typePicture ? undefined : "30px",
              whiteSpace: "pre",
              border: isUiModeDeveloper() ? "1px solid gray" : undefined,
            }}
          >
            <ProductAdminInfo knxContext={knxContext} refId={refId} instanceId={instanceId} />
            {element}
            {isUiModeProductAdmin() && debugElement}
          </div>
        );

        return undefined;
      }
    }
  }
  return element;
}

export default function ParameterRefRef(props: {
  knxContext: KnxContext;
  parentChannel: Channel;
  xchannels: Array<Channel>;
  title: string;
  data: any;
  args: any;
  repeatindex?: number;
  params: any;
  instanceId: string;
  moduleId: string;
  chapterId: string;
  parentKnxChapter: KnxChapterItem | undefined;
  nestedName: string;
  nestedText: string;
  indent: number;
  hidden: boolean;
  propertyList?: Array<any>;
}) {
  const { knxContext, data: parameterRefRef, instanceId, parentKnxChapter, moduleId } = props;

  // if (parameterRefRef == undefined) {
  //   return null;
  // }

  // const parameterRef = knxContext.getDataItem(parameterRefRef.a.RefId);
  // if (!parameterRef) {
  //   logger.log(`no ParameterRef for ParameterRefRef ${parameterRefRef.a.Id}`);
  // }

  // const InitialValueRefRef = parameterRefRef.a.Value || parameterRefRef.a.InitialValue;
  // const InitialValueRef = parameterRef.a.Value || parameterRef.a.InitialValue;

  // if (InitialValueRefRef) {
  //   knxContext.setValueNoRerender(
  //     ValueLevels.default,
  //     InitialValueRefRef,
  //     parameterRefRef.a.RefId,
  //     instanceId,
  //     moduleId,
  //   );
  // } else if (InitialValueRef != undefined) {
  //   knxContext.setValueNoRerender(ValueLevels.default, InitialValueRef, parameterRefRef.a.RefId, instanceId, moduleId);
  // } else {
  //   const parameter = knxContext.getDataItem(parameterRef?.a.RefId);
  //   if (parameter) {
  //     // console.log("mwe1: parameter = " + parameter["#name"]1);
  //   } else {
  //     logger.log(`no parameter for ${parameterRefRef.a.Id} => ${parameterRef?.a.Id}`);
  //   }
  //   const InitialParameter = parameter.a.Value || parameter.a.InitialValue;
  //   if (InitialParameter != undefined) {
  //     knxContext.setValueNoRerender(
  //       ValueLevels.default,
  //       InitialParameter,
  //       parameterRefRef.a.RefId,
  //       instanceId,
  //       moduleId,
  //     );
  //   }
  // }

  if (parentKnxChapter) {
    parentKnxChapter.propertyList.push(parameterRefRef);
  }

  let res: any = null;
  EP(
    "KnxParameterViewer-Parameter", // no itx
  ).run(() => {
    res = Parameter({ ...props, data: parameterRefRef });
  });

  return res;
}
